import { gql } from "generated-graphql";
import {
  PoliceDepartmentPickerFragment,
  PoliceDepartmentsPickerQuery,
} from "generated-graphql/graphql";
import { forwardRef, useState, useEffect } from "react";
import { ControllerFieldState } from "react-hook-form";
import AsyncAutocomplete from "./Forms/AsyncAutocomplete";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import LaunchOutlined from "@mui/icons-material/LaunchOutlined";
import { useQuery } from "@apollo/client";
import { GET_NJ_POLICE_DEPARTMENT } from "../routes/Staff/Agencies/NJPoliceDepartments/schema";

// Base props for the picker
type PoliceDepartmentPickerProps = {
  defaultSearch?: string;
  onChange?: (value: PoliceDepartmentPickerFragment | null) => void;
  value?: PoliceDepartmentPickerFragment | { id: string | number } | null;
  disabled?: boolean;
  helperText?: string | React.JSX.Element;
  displayAgencyLink?: boolean;
  label?: string;
} & ControllerFieldState;

// GraphQL fragments and queries
const POLICE_DEPARTMENT_PICKER_FRAGMENT = gql(`
  fragment PoliceDepartmentPicker on PoliceDepartment {
    id
    name
    address
    city
    state
    zip
    email
    contactName
    attn
    additionalComments
  }
`);

const POLICE_DEPARTMENTS_SEARCH_QUERY = gql(`
  query PoliceDepartmentsPicker($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!], $tenantId: String) {
    policeDepartments(search: $search, page: $page, pageSize: $pageSize, sort: $sort, tenantId: $tenantId) {
      items {
        ...PoliceDepartmentPicker
      }
      count
    }
  }
`);

// Function to format Police Department option labels
const getPoliceDepartmentOptionLabel = (
  policeDepartment: PoliceDepartmentPickerFragment
) => {
  return `${policeDepartment.name} ${policeDepartment.city}, ${policeDepartment.state}`;
};

// Function to get items from query response
const getPoliceDepartmentItems = (data: PoliceDepartmentsPickerQuery) =>
  data.policeDepartments?.items ?? [];

// Main picker component
export const PoliceDepartmentPicker = forwardRef<
  HTMLDivElement,
  PoliceDepartmentPickerProps
>(function PoliceDepartmentPicker(
  {
    onChange,
    value,
    error,
    disabled,
    defaultSearch,
    helperText,
    displayAgencyLink,
    label,
  },
  ref
) {
  // Type guard for PoliceDepartmentPickerFragment
  function isPoliceDepartmentPickerFragment(
    obj: any
  ): obj is PoliceDepartmentPickerFragment {
    return (
      obj &&
      typeof obj === "object" &&
      "name" in obj &&
      "address" in obj &&
      "city" in obj
    );
  }

  // Fetch by ID if needed
  const needsFetchById =
    value &&
    typeof value === "object" &&
    "id" in value &&
    !isPoliceDepartmentPickerFragment(value);
  const { data: fetchedById } = useQuery(GET_NJ_POLICE_DEPARTMENT, {
    variables: { id: needsFetchById ? Number((value as any).id) : -1 },
    skip: !needsFetchById,
    fetchPolicy: "cache-and-network",
  });

  // Local state for hydrated value
  const [hydratedValue, setHydratedValue] =
    useState<PoliceDepartmentPickerFragment | null>(
      isPoliceDepartmentPickerFragment(value) ? value : null
    );

  // Hydrate the value if only an id is provided
  useEffect(() => {
    if (isPoliceDepartmentPickerFragment(value)) {
      setHydratedValue(value);
    } else if (fetchedById && fetchedById.policeDepartment) {
      setHydratedValue(fetchedById.policeDepartment);
    } else {
      setHydratedValue(null);
    }
  }, [value, fetchedById]);

  return (
    <AsyncAutocomplete
      ref={ref}
      query={POLICE_DEPARTMENTS_SEARCH_QUERY}
      additionalSearchString={defaultSearch}
      variables={{
        sort: [{ field: "name", sort: "asc" }],
      }}
      getItems={getPoliceDepartmentItems}
      getOptionLabel={getPoliceDepartmentOptionLabel}
      onChange={(val) => {
        if (isPoliceDepartmentPickerFragment(val)) {
          onChange?.(val);
        } else {
          onChange?.(null);
        }
      }}
      value={hydratedValue}
      error={error}
      disabled={disabled}
      placeholderLabel={label}
      helperText={helperText}
      additionalEndAdornment={
        isPoliceDepartmentPickerFragment(hydratedValue) && displayAgencyLink
          ? agencyLinkAdornment(hydratedValue)
          : undefined
      }
    />
  );
});

function agencyLinkAdornment(
  value: PoliceDepartmentPickerFragment | null | undefined
) {
  return (
    value && (
      <Tooltip title="Open Police Department">
        <IconButton
          size="small"
          sx={{
            padding: "4px",
            color: "primary.main",
          }}
          onClick={(e) => {
            e.stopPropagation(); // Prevent triggering the dropdown
            window.open(
              `/staff/agencies/njPoliceDepartments?omnisearch=id:${value.id}`,
              "_blank"
            );
          }}
        >
          <LaunchOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
    )
  );
}
